import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import '@alltrails/types/RecurlyPlan';
import '@alltrails/types/SubscriptionTier';
import along from '@turf/along';
import { type LineString, lineString } from '@turf/helpers';
import isEqual from 'lodash.isequal';
import type RouteAssistantSplit from '@alltrails/amplitude/enums/RouteAssistantSplit';
import type WaypointEntryPoint from '@alltrails/amplitude/enums/WaypointEntryPoint';
import type WaypointSourceView from '@alltrails/amplitude/enums/WaypointSourceView';
import reorderArray from '@alltrails/core/utils/reorderArray';
import type {
  Costings,
  CostingsConfig,
  CustomRouteActivityType,
  CustomRouteAlert,
  CustomRouteMap,
  CustomRouteSegmentIndex,
  CustomRouteWaypoint,
  CustomRouteWaypointId,
  MarkerProperties
} from '@alltrails/types/CustomRoute';
import type { MapPhoto } from '@alltrails/types/Map';
import type { Visibility } from '@alltrails/types/Visibility';
import type { WaypointCategory } from '@alltrails/types/Waypoint';
import type { CustomRouteDetails, CustomRoutePath, CustomRouteSegment, CustomRouteSegmentAlert } from '../types/CustomRoute';
import type { ElevationPoint, LatLng } from '../types/Geo';
import { getLineColorBySegmentIndex, getNextColor } from '../utils/customRoutes/colors';
import { WAYPOINT_DECIMAL_PLACES } from '../utils/customRoutes/constants';
import { isLinkedToNextSegment, isLinkedToPreviousSegment } from '../utils/customRoutes/segmentLinking';
import { doEndsCoincide, isSinglePointSegment } from '../utils/customRoutes/validation';
import type { Metadata, SegmentMetadata } from './customRoutesApi';

export const UNDO_LIMIT = 100;
export const customRouteDefaultActivity = 'hiking';

export type ToastMessage = {
  iconCode?: string;
  message: string;
};

type WaypointData = {
  id: CustomRouteWaypointId;
  latitude: number;
  longitude: number;
  name: string;
  description?: string;
  showTitle?: boolean;
  waypointCategory?: WaypointCategory;
  contentPrivacy?: Visibility;
  isGlobal?: boolean;
};

type RouteState = {
  aiGeneratedDescriptionsSegmentsSnapshot?: string;
  segments: CustomRouteSegment[];
  activityType: CustomRouteActivityType;
  costings?: Costings;
  waypoints?: WaypointData[];
};

type CutData = {
  targetLatLng: LatLng;
  endIndex: number;
  startIndex: number;
  existingSegment: CustomRouteSegment;
  existingSegmentIndex: number;
  existingUndoStack: RouteState[];
  existingRedoStack: RouteState[];
};

export type CustomRouteState = {
  aiGeneratedDescriptionsSegmentsSnapshot?: string; // Used to determine if ai-generated segment descriptions are out-dated
  isEditing?: boolean;
  isEditingWaypoint?: boolean;
  isSmartRouting: boolean;
  isRoutingAlongSuggestedRoute: boolean;
  isTrailMap: boolean;
  /**
   * True when the loaded recording is served from an anonymized slug (e.g. `community-content-*`).
   * The owner's identity is stripped from `map`, and identity-bearing sub-fetches (tagged users) must be suppressed.
   */
  isAnonymized?: boolean;
  activeSegmentIndex?: CustomRouteSegmentIndex;
  hoveredSegmentIndex?: CustomRouteSegmentIndex;
  selectedHandleIndex?: number;
  nextDndId: number;
  nextSegmentColor?: string;
  activeWaypointId?: CustomRouteWaypointId;
  selectedWaypointId?: CustomRouteWaypointId;
  viewingWaypointId?: CustomRouteWaypointId;
  viewingWaypointEntryPoint?: WaypointEntryPoint;
  viewingWaypointSourceView?: WaypointSourceView;
  nextWaypointId?: CustomRouteWaypointId;
  undoStack: RouteState[];
  redoStack: RouteState[];
  route: CustomRouteDetails;
  map?: CustomRouteMap;
  metadata?: { created: string; updated: string };
  toastMessage?: ToastMessage;
  toastAlerts: CustomRouteAlert[];
  draftWaypoint?: Partial<CustomRouteWaypoint> | null;
  waypointEditStartPosition?: { id: CustomRouteWaypointId; latitude: number; longitude: number } | null;
  costingsConfig?: CostingsConfig;
  costings?: Costings;
  /**
   * Bumped whenever a segment is reversed. Used as a React key on the active
   * polyline source to force Mapbox to recompute symbol placement so polyline
   * chevrons are re-added to the map with the correct direction.
   */
  reverseCount: number;
  suggestedRoute?: LineString;
  isReplacingRoute?: boolean;
  splitType?: RouteAssistantSplit; // Used for analytic event on save
  routeEditedAfterSplit?: boolean; // Used for analytic event on save
  editEndpointMode?: boolean;
  pendingEndpointCrop?: EndpointCrop | null;
  isRouteBuildingEnabled: boolean; // Custom route builder mode: true = building (clicks place points), false = interacting (clicks open overlay popups)
  cutData?: CutData;
};

export type EndpointCrop = {
  atLineSegmentId: number;
  cropIndex: number;
  newEndPoint?: LatLng;
};

export const initialState: CustomRouteState = {
  aiGeneratedDescriptionsSegmentsSnapshot: undefined,
  isEditing: undefined,
  isEditingWaypoint: undefined,
  isSmartRouting: true,
  isRoutingAlongSuggestedRoute: false,
  isTrailMap: false,
  isAnonymized: false,
  activeSegmentIndex: undefined,
  hoveredSegmentIndex: undefined,
  selectedHandleIndex: undefined,
  nextSegmentColor: getLineColorBySegmentIndex(0),
  activeWaypointId: undefined,
  selectedWaypointId: undefined,
  viewingWaypointId: undefined,
  viewingWaypointEntryPoint: undefined,
  viewingWaypointSourceView: undefined,
  nextWaypointId: 0,
  undoStack: [],
  redoStack: [],
  route: {
    bounds: undefined,
    activityType: 'hiking',
    id: undefined,
    distance: 0,
    elevationGain: 0,
    elevationLoss: 0,
    estimatedTime: undefined,
    segments: [],
    waypoints: [],
    createdAt: undefined,
    garminCourse: undefined,
    slug: '',
    name: '',
    description: '',
    contentPrivacy: undefined,
    location: {
      latitude: '',
      longitude: ''
    }
  },
  map: undefined,
  metadata: undefined,
  toastMessage: undefined,
  toastAlerts: [],
  draftWaypoint: undefined,
  waypointEditStartPosition: undefined,
  costingsConfig: undefined,
  costings: undefined,
  nextDndId: 0,
  reverseCount: 0,
  suggestedRoute: undefined,
  isReplacingRoute: false,
  editEndpointMode: false,
  pendingEndpointCrop: null,
  isRouteBuildingEnabled: true,
  cutData: undefined
};

const getSegmentsPathSnapshots = (segments: CustomRouteSegment[]) => {
  return JSON.stringify(segments.map(segment => segment.paths));
};

export const selectCurrentSegmentsPathSnapshots = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  segments => getSegmentsPathSnapshots(segments)
);

export const selectIsReplacingRoute = (state: { customRoute: CustomRouteState }) => state.customRoute.isReplacingRoute === true;

export const selectIsDrawingEnabled = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.isEditingWaypoint,
  (state: { customRoute: CustomRouteState }) => state.customRoute.isEditing,
  (state: { customRoute: CustomRouteState }) => state.customRoute.activeSegmentIndex,
  (state: { customRoute: CustomRouteState }) => state.customRoute.isReplacingRoute,
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (isEditingWaypoint, isEditing, activeSegmentIndex, isReplacingRoute, segments) => {
    if (isEditingWaypoint) {
      return false;
    }

    if (!isEditing) {
      return false;
    }

    if (activeSegmentIndex !== undefined) {
      return true;
    }

    if (isReplacingRoute) {
      return true;
    }

    return segments.length === 0;
  }
);

// The cursor the map should show when nothing more specific (a hovered pin, POI, etc.) applies:
// crosshair only while actively building a route (drawing context AND route building enabled),
// otherwise the default. In View mode (route building off) clicks don't place points, so we must
// not show the crosshair. Interaction handlers should restore this on mouse-leave rather than
// hardcoding a reset value, so the drawing crosshair isn't lost.
export const selectRestingCursor = createSelector(
  selectIsDrawingEnabled,
  (state: { customRoute: CustomRouteState }) => state.customRoute.isRouteBuildingEnabled,
  (isDrawingEnabled, isRouteBuildingEnabled) => (isDrawingEnabled && isRouteBuildingEnabled ? 'crosshair' : 'unset')
);

export const selectCustomRouteActiveSegment = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.activeSegmentIndex,
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (activeSegmentIndex, segments) => (activeSegmentIndex !== undefined ? segments[activeSegmentIndex] : undefined)
);

export const selectCustomRouteInactiveSegments = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.activeSegmentIndex,
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (activeSegmentIndex, segments) => segments.filter((_, index) => index !== activeSegmentIndex)
);

export const selectCustomRouteSegmentIndexes = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  segments => segments.map((_, index) => index)
);

export const selectCustomRouteSegmentDndIds = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  segments => segments.map(segment => segment.dndId)
);

export const selectCustomRouteSegmentServerIds = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  segments => segments.map(segment => segment.serverId)
);

export const selectCustomRouteSegmentByIndex = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (_: any, index: CustomRouteSegmentIndex) => index,
  (segments: CustomRouteSegment[], index: CustomRouteSegmentIndex) => segments[index]
);

export const selectCustomRouteSegmentByDndId = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (_: any, dndId: string) => dndId,
  (segments: CustomRouteSegment[], dndId: string) => segments.find(segment => segment.dndId === dndId)
);

export const selectCustomRouteSegmentsPaths = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (segments: CustomRouteSegment[]) => segments.map(segment => segment.paths)
);

export const selectEndsCoincide = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  segments => doEndsCoincide(segments) ?? false
);

export const selectCustomRouteElevationData = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (segments: CustomRouteSegment[]) => {
    let distanceBeforeCurrentSegment = 0;

    return segments.map(segment => {
      const elevationPoints = segment.elevationPoints?.map(
        ([distanceTo, ...rest]) => [distanceTo + distanceBeforeCurrentSegment, ...rest] as ElevationPoint
      );
      distanceBeforeCurrentSegment += segment.distance || 0;

      return elevationPoints || [];
    });
  }
);

export const selectCustomRouteWaypointIds = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.waypoints,
  waypoints => waypoints.map(waypoint => waypoint.id)
);

export const selectCustomRouteWaypointServerIds = createSelector(
  // Optional because the customRoute slice is not loaded in every app (e.g. trail-app).
  (state: { customRoute?: CustomRouteState }) => state.customRoute?.route.waypoints,
  waypoints => waypoints?.map(waypoint => waypoint.serverId) ?? []
);

export const selectCustomRouteWaypointById = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.waypoints,
  (_: any, id: CustomRouteWaypointId) => id,
  (waypoints: CustomRouteWaypoint[], id: CustomRouteWaypointId) => waypoints.find(waypoint => waypoint.id === id)
);

export const getMarkerSelectionProperties = (
  marker: Pick<MarkerProperties, 'isStart' | 'isEnd' | 'pathIndex' | 'endPathIndex'>,
  selectedHandleIndex?: number
) => {
  const { isStart, isEnd, pathIndex, endPathIndex } = marker;
  const isStartEnd = isStart && isEnd;
  const isCombinedStart = isStartEnd && selectedHandleIndex === 0;
  const isCombinedEnd = isStartEnd && selectedHandleIndex !== 0;
  const isStartOnly = isStart && !isEnd;
  const isEndOnly = !isStart && isEnd;

  return { isStart: isStartOnly || isCombinedStart, isEnd: isEndOnly || isCombinedEnd, pathIndex: isCombinedEnd ? endPathIndex! : pathIndex };
};

const updateRouteMetadata = (state: CustomRouteState) => {
  if (!state.route.segments.length || state.route.segments.every(segment => segment.paths.length === 0 || isSinglePointSegment(segment))) {
    state.route.distance = 0;
    state.route.elevationGain = 0;
    state.route.elevationLoss = 0;
    state.route.estimatedTime = undefined;
  }
};

const updateSegmentMetadata = (segment: CustomRouteSegment) => {
  // Reset segment metadata if the last path was removed or the last path was reduced to one point
  if (segment.paths.length === 0 || isSinglePointSegment(segment)) {
    segment.elevationPoints = [];
    segment.distance = 0;
    segment.elevationGain = 0;
    segment.elevationLoss = 0;
    segment.estimatedTime = undefined;
  }
};

const updateActiveSegmentIndex = (state: CustomRouteState) => {
  if (state.activeSegmentIndex === undefined) {
    return;
  }

  const segment = state.route.segments[state.activeSegmentIndex];
  if (!segment) {
    state.activeSegmentIndex = undefined;
    return;
  }
};

const updateSelectedHandleIndex = (state: CustomRouteState, markerIndex?: number) => {
  if (!state.isEditing || state.activeSegmentIndex === undefined) {
    state.selectedHandleIndex = undefined;
    return;
  }

  const activeSegment = state.route.segments[state.activeSegmentIndex];
  if (!activeSegment || activeSegment.paths.length === 0) {
    state.selectedHandleIndex = undefined;
    return;
  }

  const paths = activeSegment.paths;
  const isSinglePoint = isSinglePointSegment(activeSegment);
  // If the segment has more than one point select the end point whose index is one more than the number of paths
  const endMarkerIndex = isSinglePoint ? 0 : paths.length;

  // If the marker index is not defined or is out of bounds, select the end point
  state.selectedHandleIndex = markerIndex !== undefined && markerIndex >= 0 && markerIndex <= endMarkerIndex ? markerIndex : endMarkerIndex;
};

const updateNextSegmentColor = (state: CustomRouteState) => {
  const segments = state.route.segments;
  state.nextSegmentColor = segments.length > 0 ? getNextColor(segments[segments.length - 1].color) : getLineColorBySegmentIndex(0);
};

const updateNextWaypoint = (state: CustomRouteState) => {
  const waypoints = state.route.waypoints;
  state.nextWaypointId = waypoints.length > 0 ? waypoints.reduce((max, waypoint) => (waypoint.id > max ? waypoint.id : max), 0) + 1 : 0;
};

const handleConnectCutSegment = (state: CustomRouteState) => {
  if (!state.cutData || state.activeSegmentIndex === undefined) {
    return;
  }

  // get the last point of the active segment
  const segment = state.route.segments[state.activeSegmentIndex];
  const lastPath = segment.paths[segment.paths.length - 1];
  const lastPoint = lastPath.points[lastPath.points.length - 1];

  if (isEqual(state.cutData.targetLatLng, lastPoint)) {
    const existingSegment = state.cutData.existingSegment;
    // replace the existing segment paths from the start index to the end index with the current segment paths
    const newSegment = { ...existingSegment, paths: existingSegment.paths.toSpliced(state.cutData.startIndex, 1, ...segment.paths) };

    state.route.segments[state.cutData.existingSegmentIndex] = newSegment;
    state.undoStack = state.cutData.existingUndoStack;
    state.redoStack = state.cutData.existingRedoStack;
    state.cutData = undefined;
  }
};

export const updateUndoRedoStacks = (state: CustomRouteState) => {
  // Since we might edit segments in place after updating the undo stack we need to save a deep copy to the undo stack
  // For waypoints, we store full data to support undoing deletions with their original names/descriptions
  // When undoing, we'll preserve current names/descriptions for existing waypoints (not deleted)

  state.undoStack.push(
    JSON.parse(
      JSON.stringify({
        aiGeneratedDescriptionsSegmentsSnapshot: state.aiGeneratedDescriptionsSegmentsSnapshot,
        segments: state.route.segments,
        activityType: state.route.activityType,
        costings: state.costings,
        waypoints: state.route.waypoints
      })
    )
  );
  state.redoStack = [];

  if (state.undoStack.length > UNDO_LIMIT) {
    state.undoStack.shift();
  }
};

const generateDndId = (state: CustomRouteState): string => `segment-dnd-${state.nextDndId++}`;

// Only reset the number-based costings as the enum-based costings are "preferences" that are set for the session
const resetCostings = (state: CustomRouteState) => {
  const costingsConfig = state.costingsConfig;

  if (!costingsConfig) {
    return;
  }

  state.costings = { ...state.costings, makeShorter: costingsConfig.makeShorter.default };
};

const handleRouteEdit = (state: CustomRouteState) => {
  if (state.splitType) {
    state.routeEditedAfterSplit = false;
  }
  resetCostings(state);
};

export const customRouteSlice = createSlice({
  name: 'customRoute',
  initialState,
  reducers: {
    resetState: (
      state: CustomRouteState,
      action: PayloadAction<{ persistKeys?: (keyof CustomRouteState)[]; initialState?: Partial<CustomRouteState> }>
    ) => {
      const defaultState: CustomRouteState = { ...initialState, ...(action.payload.initialState || {}), nextDndId: state.nextDndId };
      action.payload.persistKeys?.forEach(key => {
        const persistValue = state[key];
        (defaultState[key] as typeof persistValue) = persistValue;
      });

      return defaultState;
    },
    setEditState: (
      state,
      action: PayloadAction<{
        isEditing: boolean;
        activeSegmentIndex?: CustomRouteSegmentIndex;
        activeWaypointId?: CustomRouteWaypointId;
        hasActivePopupOverlay?: boolean;
      }>
    ) => {
      const { isEditing, activeSegmentIndex, activeWaypointId, hasActivePopupOverlay } = action.payload;
      const wasEditing = state.isEditing;
      state.isEditing = isEditing;
      state.cutData = undefined;
      state.viewingWaypointId = undefined;

      // When entering edit mode, default a missing activity (matching the new-route default and the
      // upload selector). This lets routing metadata (distance/elevation) be fetched for routes that arrived
      // without an activity
      if (isEditing && !state.route.activityType) {
        state.route.activityType = customRouteDefaultActivity;
      }

      if (activeWaypointId !== undefined) {
        const waypoint = state.route.waypoints.find(waypoint => waypoint.id === activeWaypointId);
        if (!waypoint) {
          return;
        }

        state.activeWaypointId = activeWaypointId;
        state.selectedWaypointId = activeWaypointId;
        state.isEditingWaypoint = true;
        state.waypointEditStartPosition = {
          id: activeWaypointId,
          latitude: waypoint.latitude,
          longitude: waypoint.longitude
        };
      }

      if (activeSegmentIndex !== undefined) {
        state.activeSegmentIndex = activeSegmentIndex;
      }

      if (!isEditing) {
        state.activeSegmentIndex = undefined;
        state.isReplacingRoute = false;
        resetCostings(state);
      }

      if (!isEditing || (isEditing && !wasEditing)) {
        updateSelectedHandleIndex(state);
      }

      if (isEditing && hasActivePopupOverlay) {
        state.isRouteBuildingEnabled = false;
      }
    },
    setIsSmartRouting: (state, action: PayloadAction<boolean>) => {
      state.isSmartRouting = action.payload;
    },
    setRoute: (
      state,
      action: PayloadAction<{
        route: CustomRouteDetails;
        map?: CustomRouteMap;
        persistEditState?: boolean;
        isAnonymized?: boolean;
      }>
    ) => {
      const { route, map, persistEditState, isAnonymized } = action.payload;
      state.route = {
        ...route,
        segments: route.segments.map((segment, index) => ({
          ...segment,
          dndId: segment.dndId || generateDndId(state),
          color: segment.color || getLineColorBySegmentIndex(index)
        }))
      };
      state.map = map;
      state.isAnonymized = isAnonymized ?? false;

      if (map?.metadata) {
        state.metadata = map.metadata;
      }

      if (!persistEditState) {
        state.isEditing = false;
        state.isReplacingRoute = false;
        state.editEndpointMode = false;
        state.pendingEndpointCrop = null;
      }
      state.activeSegmentIndex = undefined;
      state.selectedHandleIndex = undefined;
      state.activeWaypointId = undefined;
      state.selectedWaypointId = undefined;
      state.cutData = undefined;

      updateNextSegmentColor(state);
      updateNextWaypoint(state);
      resetCostings(state);
    },
    setCutIndex: (state, action: PayloadAction<number>) => {
      if (state.activeSegmentIndex === undefined) {
        return;
      }

      const segment = state.route.segments[state.activeSegmentIndex];
      if (!segment) {
        return;
      }

      updateUndoRedoStacks(state);

      state.cutData = {
        endIndex: action.payload,
        startIndex: action.payload - 1,
        targetLatLng: segment.paths[action.payload].points[0],
        existingSegment: segment,
        existingSegmentIndex: state.activeSegmentIndex,
        existingUndoStack: state.undoStack,
        existingRedoStack: state.redoStack
      };

      state.undoStack = [];
      state.redoStack = [];

      // set route to an empty route for drawing new paths to connect the cut section of the active segment
      // the start point of the new route is the same lat/lng as the the start point of the cut section
      const newSegment = {
        ...segment,
        paths: [{ points: [segment.paths[action.payload - 1].points[0]] }]
      };
      state.route.segments[state.activeSegmentIndex] = newSegment;
      state.selectedHandleIndex = 0;
    },
    updateRoute: (
      state,
      action: PayloadAction<{
        route: CustomRouteDetails;
        map: CustomRouteMap;
      }>
    ) => {
      const { route, map } = action.payload;
      state.map = map;

      const { id, slug, name, description, segments, waypoints, createdAt } = route;

      // Fields that will never be more up to date locally than on the server
      state.route.id = id;
      state.route.slug = slug;
      state.route.createdAt = createdAt;

      // Fields that if not set locally will be set to the server value
      state.route.name ||= name;
      state.route.description ||= description;

      // Add any new segments and waypoints to the route after any existing segments and waypoints

      const localSegmentServerIds = state.route.segments.map(segment => segment.serverId);
      const newSegments = segments.filter(segment => !localSegmentServerIds.includes(segment.serverId));
      newSegments.forEach(segment => {
        const newSegment = { ...segment, dndId: generateDndId(state), color: segment.color || state.nextSegmentColor! };
        state.route.segments.push(newSegment);
        updateNextSegmentColor(state);
      });

      const localWaypointServerIds = state.route.waypoints.map(waypoint => waypoint.serverId);
      const newWaypoints = waypoints.filter(waypoint => !localWaypointServerIds.includes(waypoint.serverId));
      newWaypoints.forEach(waypoint => {
        // longitude and latitude round to 6 decimal places
        const latitude = Number(waypoint.latitude.toFixed(WAYPOINT_DECIMAL_PLACES));
        const longitude = Number(waypoint.longitude.toFixed(WAYPOINT_DECIMAL_PLACES));
        const newWaypoint = { ...waypoint, id: state.nextWaypointId!, latitude, longitude };
        state.route.waypoints.push(newWaypoint);
        updateNextWaypoint(state);

        return newWaypoint;
      });

      handleRouteEdit(state);
    },
    setActiveSegmentIndex: (state, action: PayloadAction<{ activeSegmentIndex: CustomRouteSegmentIndex | undefined; canEdit: boolean }>) => {
      const { activeSegmentIndex, canEdit } = action.payload;

      // Never allow de-selecting an active segment while editing a custom route
      if (activeSegmentIndex === undefined && state.isEditing) {
        return;
      }

      state.activeSegmentIndex = activeSegmentIndex;

      if (activeSegmentIndex !== undefined && canEdit) {
        state.isEditing = true;
      }

      updateSelectedHandleIndex(state);
      resetCostings(state);
    },
    setHoveredSegmentIndex: (state, action: PayloadAction<CustomRouteSegmentIndex | undefined>) => {
      state.hoveredSegmentIndex = action.payload;
    },
    setSelectedHandleIndex: (state, action: PayloadAction<number | undefined>) => {
      state.selectedHandleIndex = action.payload;
    },
    updateRouting: (
      state,
      action: PayloadAction<{
        activityType?: CustomRouteActivityType;
        segment?: CustomRouteSegment;
        segmentIndex?: CustomRouteSegmentIndex;
        segments?: CustomRouteSegment[];
        costings?: Costings;
      }>
    ) => {
      const { activityType, segment, segmentIndex, segments, costings } = action.payload;

      if (!activityType && !segment && !segments && !costings) {
        return;
      }

      updateUndoRedoStacks(state);

      if (activityType) {
        state.route.activityType = activityType;
      }

      // If a segment is provided, update the segment at the given index (defaults to the active segment)
      if (segment) {
        const targetIndex = segmentIndex ?? state.activeSegmentIndex;
        if (targetIndex !== undefined) {
          const currentSegment = state.route.segments[targetIndex];
          if (currentSegment) {
            Object.assign(currentSegment, segment);
          }
        }
      }

      // If an array of segments is provided, overwrite the existing segments with the new segments
      if (segments) {
        state.route.segments = segments;
        updateNextSegmentColor(state);
        updateRouteMetadata(state);
        updateSelectedHandleIndex(state);
      }

      handleRouteEdit(state);

      if (costings) {
        state.costings = costings;
      }
    },
    updateIsRoutingAlongSuggestedRoute: (state, action: PayloadAction<boolean>) => {
      state.isRoutingAlongSuggestedRoute = action.payload;
    },
    setRouteDetails: (state, action: PayloadAction<{ name: string; description?: string; contentPrivacy: Visibility }>) => {
      const { name, description, contentPrivacy } = action.payload;
      state.isEditing = true;
      state.route.name = name;
      state.route.description = description;
      state.route.contentPrivacy = contentPrivacy;
    },
    setRoutePrivacy: (state, action: PayloadAction<CustomRouteMap['contentPrivacy']>) => {
      state.route.contentPrivacy = action.payload;
    },
    // Applies a created or edited review (rating, comment, difficulty, condition tags) back onto the
    // loaded recording so RecordingReview re-renders immediately without refetching the map.
    updateRecordingReview: (
      state,
      action: PayloadAction<
        Pick<CustomRouteMap, 'rating' | 'comment' | 'difficulty' | 'ratingAttributes' | 'infoAttributes' | 'obstacles' | 'associatedTrailReviewId'>
      >
    ) => {
      if (!state.map) {
        return;
      }
      const { rating, comment, difficulty, ratingAttributes, infoAttributes, obstacles, associatedTrailReviewId } = action.payload;
      state.map.rating = rating;
      state.map.comment = comment;
      state.map.difficulty = difficulty;
      state.map.ratingAttributes = ratingAttributes;
      state.map.infoAttributes = infoAttributes;
      state.map.obstacles = obstacles;
      if (associatedTrailReviewId !== undefined) {
        state.map.associatedTrailReviewId = associatedTrailReviewId;
      }
    },
    // Replaces the loaded recording's photos with a freshly fetched list so the photo grid and
    // accordion re-render immediately after an upload without a full page reload.
    setRecordingPhotos: (state, action: PayloadAction<MapPhoto[]>) => {
      if (!state.map) {
        return;
      }
      state.map.mapPhotos = action.payload;
    },
    setIsTrailMap: (state, action: PayloadAction<boolean>) => {
      state.isTrailMap = action.payload;
    },
    addSegment: (state, action: PayloadAction<Partial<Omit<CustomRouteSegment, 'color'>> | undefined>) => {
      updateUndoRedoStacks(state);
      state.isEditing = true;

      const color = state.nextSegmentColor || getLineColorBySegmentIndex(0);
      const distance = action.payload?.distance || 0;
      const elevationGain = action.payload?.elevationGain || 0;
      const elevationLoss = action.payload?.elevationLoss || 0;
      const elevationPoints = action.payload?.elevationPoints || [];
      const paths = action.payload?.paths || [];
      state.route.segments.push({
        dndId: generateDndId(state),
        color,
        distance,
        elevationGain,
        elevationLoss,
        elevationPoints,
        paths
      });

      state.activeSegmentIndex = state.route.segments.length - 1;
      state.nextSegmentColor = getNextColor(color);

      updateSelectedHandleIndex(state);
      handleRouteEdit(state);
    },
    splitActiveSegmentAtSelectedHandle: state => {
      const segments = state.route.segments;
      const activeSegmentIndex = state.activeSegmentIndex;
      const selectedHandleIndex = state.selectedHandleIndex;

      // Must not be the start or end handle
      if (
        activeSegmentIndex === undefined ||
        selectedHandleIndex === undefined ||
        selectedHandleIndex === 0 ||
        selectedHandleIndex === segments[activeSegmentIndex].paths.length
      ) {
        return;
      }

      updateUndoRedoStacks(state);

      const activeSegment = segments[activeSegmentIndex];
      const updatedActiveSegment = { ...activeSegment, isLinkedToNextSegment: true, paths: activeSegment.paths.slice(0, selectedHandleIndex) };

      // Find a color that will not conflict with the surrounding segments
      let newColor = getNextColor(activeSegment.color);
      const nextSegment = segments[activeSegmentIndex + 1];
      if (nextSegment && nextSegment.color === newColor) {
        newColor = getNextColor(nextSegment.color);
      }
      const newSegment = {
        dndId: generateDndId(state),
        color: newColor,
        isLinkedToNextSegment: activeSegment.isLinkedToNextSegment,
        paths: activeSegment.paths.slice(selectedHandleIndex)
      };

      state.route.segments = [...segments.slice(0, activeSegmentIndex), updatedActiveSegment, newSegment, ...segments.slice(activeSegmentIndex + 1)];

      // Select the new segment
      state.activeSegmentIndex = activeSegmentIndex + 1;
      updateSelectedHandleIndex(state, 0);

      if (!nextSegment) {
        // If the new segment is now the last segment, update the next color
        state.nextSegmentColor = getNextColor(newColor);
      }

      handleRouteEdit(state);
    },
    removeSegment: (state, action: PayloadAction<CustomRouteSegmentIndex>) => {
      const removeIndex = action.payload;

      if (removeIndex < 0 || removeIndex >= state.route.segments.length) {
        return;
      }

      updateUndoRedoStacks(state);
      state.isEditing = true;

      state.route.segments.splice(removeIndex, 1);

      // If the last segment was removed update the next color based on the new last segment
      if (removeIndex === state.route.segments.length) {
        updateNextSegmentColor(state);
      }

      if (state.activeSegmentIndex === removeIndex) {
        // If the active segment was removed, clear the active segment and reset selected handle
        state.activeSegmentIndex = undefined;
        updateSelectedHandleIndex(state);
      } else if (state.activeSegmentIndex !== undefined && state.activeSegmentIndex > removeIndex) {
        // If the active segment is after the removed segment, re-index it
        state.activeSegmentIndex = state.activeSegmentIndex - 1;
      }

      // Unlink the previous segment
      if (removeIndex > 0) {
        const previousSegment = state.route.segments[removeIndex - 1];
        if (previousSegment.isLinkedToNextSegment) {
          previousSegment.isLinkedToNextSegment = false;
        }
      }

      updateRouteMetadata(state);
      handleRouteEdit(state);
    },
    reorderSegments: (state, action: PayloadAction<{ startIndex: number; endIndex: number }>) => {
      const { startIndex, endIndex } = action.payload;

      if (startIndex === endIndex) {
        return;
      }

      updateUndoRedoStacks(state);

      // Unlink the segment that is being moved and the segment before it
      const unlinkIndices = [startIndex, startIndex - 1];
      if (startIndex < endIndex) {
        // Moved forward: unlink the segment at the end index (before reordering)
        unlinkIndices.push(endIndex);
      } else {
        // Moved backward: unlink the segment before the end index (before reordering)
        unlinkIndices.push(endIndex - 1);
      }
      unlinkIndices.forEach(index => {
        if (index >= 0) {
          const segment = state.route.segments[index];
          if (segment && segment.isLinkedToNextSegment) {
            segment.isLinkedToNextSegment = false;
          }
        }
      });

      state.route.segments = reorderArray(state.route.segments, startIndex, endIndex);

      // Update activeSegmentIndex if it was affected by the reorder
      if (state.activeSegmentIndex !== undefined) {
        if (state.activeSegmentIndex === startIndex) {
          state.activeSegmentIndex = endIndex;
        } else if (startIndex < endIndex) {
          // Moved forward: segments between start+1..end shift back by 1
          if (state.activeSegmentIndex > startIndex && state.activeSegmentIndex <= endIndex) {
            state.activeSegmentIndex = state.activeSegmentIndex - 1;
          }
        } else {
          // Moved backward: segments between end..start-1 shift forward by 1
          if (state.activeSegmentIndex >= endIndex && state.activeSegmentIndex < startIndex) {
            state.activeSegmentIndex = state.activeSegmentIndex + 1;
          }
        }
      }

      updateRouteMetadata(state);
      handleRouteEdit(state);
    },
    replaceSegments: (state, action: PayloadAction<{ newActivityType?: CustomRouteActivityType; segments: CustomRouteSegment[] }>) => {
      updateUndoRedoStacks(state);

      const { newActivityType, segments } = action.payload;
      if (newActivityType) {
        state.route.activityType = newActivityType;
      }
      state.route.segments = segments.map(segment => ({ ...segment, dndId: generateDndId(state) }));

      state.activeSegmentIndex = undefined;
      state.selectedHandleIndex = undefined;
      updateNextSegmentColor(state);
      handleRouteEdit(state);
    },
    replaceSegmentDescriptionsAndAlerts: (
      state,
      action: PayloadAction<{ data: { alerts?: CustomRouteSegmentAlert[]; description: string }[]; descriptionsAreAiGenerated: boolean }>
    ) => {
      const { data, descriptionsAreAiGenerated } = action.payload;
      if (data.length !== state.route.segments.length) {
        return;
      }
      updateUndoRedoStacks(state);
      state.route.segments = state.route.segments.map((segment, index) => {
        const { alerts, description } = data[index];
        return { ...segment, alerts, description, descriptionIsAiGenerated: descriptionsAreAiGenerated };
      });
    },
    setSegmentDetails: (
      state,
      action: PayloadAction<{
        index: CustomRouteSegmentIndex;
        name: string;
        description: string;
        color: string;
      }>
    ) => {
      const { index, name, description, color } = action.payload;
      const segment = state.route.segments[index];

      if (!segment) {
        return;
      }

      segment.name = name || undefined;
      segment.color = color;

      if (description !== segment.description) {
        segment.description = description || undefined;
        segment.descriptionIsAiGenerated = false;
      }

      if (index === state.route.segments.length - 1) {
        state.nextSegmentColor = getNextColor(color);
      }
    },
    addActiveSegmentPath: (state, action: PayloadAction<CustomRoutePath>) => {
      const { points, freeDraw } = action.payload;
      const activeSegmentIndex = state.activeSegmentIndex;

      if (activeSegmentIndex === undefined) {
        return;
      }

      const activeSegment = state.route.segments[activeSegmentIndex];

      if (!activeSegment) {
        return;
      }

      const paths = activeSegment.paths;
      const selectedHandleIndex = state.selectedHandleIndex;

      if (paths.length > 0 && (selectedHandleIndex === undefined || selectedHandleIndex > paths.length)) {
        return;
      }

      updateUndoRedoStacks(state);

      let newSelectedHandleIndex;

      if (paths.length === 0) {
        // If this is the first point in the segment, create a new path
        paths.push({ points, freeDraw });
      } else if (isSinglePointSegment(activeSegment)) {
        // If the active segment has only one path with one point, add the points of the new path to that path
        paths[0].points.push(...points);
        paths[0].freeDraw = freeDraw;
      } else {
        const isStart = selectedHandleIndex === 0;
        const isEnd = selectedHandleIndex === paths.length;
        // The end handle is at the end of the last path rather than at the beginning of a path
        const pathIndex = isEnd ? selectedHandleIndex - 1 : selectedHandleIndex!;
        const path = paths[pathIndex];
        const newPoints = [...points];

        // If the last point of the new path is not the first point of the existing path, append the first point of the existing path to the new path
        if (isStart && !isEqual(newPoints[newPoints.length - 1], path.points[0])) {
          newPoints.push(path.points[0]);
        } else if (isEnd && !isEqual(newPoints[0], path.points[path.points.length - 1])) {
          // If the first point of the new path is not the last point of the existing path, prepend the last point of the existing path to the new path
          newPoints.unshift(path.points[path.points.length - 1]);
        } else if (!isStart && !isEnd && !isEqual(newPoints[0], path.points[0])) {
          // If the first point of the new path is not the first point of the existing path, prepend the first point of the existing path to the new path
          newPoints.unshift(path.points[0]);
        }

        // Insert the new path at the selected handle index
        paths.splice(selectedHandleIndex!, 0, { points: newPoints, freeDraw });

        // If not inserting at the start or end, also insert a reverse path after the new path so that the final result is that an out-and-back path has been inserted before the selected handle
        if (!isStart && !isEnd) {
          paths.splice(pathIndex + 1, 0, { points: [...newPoints].reverse(), freeDraw });
        }

        if (isEnd && activeSegment.isLinkedToNextSegment && activeSegmentIndex < state.route.segments.length - 1) {
          // Insert a reversed copy of the new path at the start of the next segment to maintain the connection between the two segments
          const nextSegment = state.route.segments[activeSegmentIndex + 1];
          nextSegment.paths.splice(0, 0, { points: [...newPoints].reverse(), freeDraw });
        } else if (isStart && activeSegmentIndex > 0) {
          const previousSegment = state.route.segments[activeSegmentIndex - 1];
          if (previousSegment.isLinkedToNextSegment) {
            // Insert a reversed copy of the new path at the end of the previous segment to maintain the connection between the two segments
            previousSegment.paths.splice(previousSegment.paths.length, 0, { points: [...newPoints].reverse(), freeDraw });
          }
        }

        // Select the newly placed handle
        // If the start handle was selected a new start handle was inserted, so select it
        // For any other handle the new handle is the next handle after the selected handle
        newSelectedHandleIndex = isStart ? 0 : selectedHandleIndex! + 1;
      }

      updateSelectedHandleIndex(state, newSelectedHandleIndex);
      handleRouteEdit(state);
      handleConnectCutSegment(state);
    },
    addActiveSegmentHandle: (
      state,
      action: PayloadAction<{ pathIndex: number; pointIndex: number; latitude: number; longitude: number; replacementPoints?: LatLng[] }>
    ) => {
      const { pathIndex, pointIndex, latitude, longitude, replacementPoints } = action.payload;

      if (state.activeSegmentIndex === undefined) {
        return;
      }

      const segment = state.route.segments[state.activeSegmentIndex];
      if (!segment) {
        return;
      }

      const path = segment.paths[pathIndex];
      if (!path) {
        return;
      }

      const points = replacementPoints || path.points;
      if (pointIndex > points.length - 1) {
        return;
      }

      updateUndoRedoStacks(state);

      const sliceIndex = pointIndex === points.length - 1 ? pointIndex : pointIndex + 1;
      const beforePoints = points.slice(0, sliceIndex);
      const afterPoints = points.slice(sliceIndex);

      segment.paths.splice(
        pathIndex,
        1,
        { points: [...beforePoints, [latitude, longitude]], freeDraw: path.freeDraw },
        {
          points: [[latitude, longitude], ...afterPoints],
          freeDraw: path.freeDraw
        }
      );

      // Select the new handle
      updateSelectedHandleIndex(state, pathIndex + 1);
      handleRouteEdit(state);
    },
    updateActiveSegmentHandle: (
      state,
      action: PayloadAction<
        Omit<MarkerProperties, 'id' | 'segmentIndex'> & {
          beforePoints?: LatLng[];
          afterPoints?: LatLng[];
        }
      >
    ) => {
      const { latitude, longitude, beforePoints: rawBeforePoints, afterPoints: rawAfterPoints } = action.payload;
      const { isStart, isEnd, pathIndex } = getMarkerSelectionProperties(action.payload, state.selectedHandleIndex);

      if (state.activeSegmentIndex === undefined) {
        return;
      }

      const segment = state.route.segments[state.activeSegmentIndex];
      if (!segment) {
        return;
      }

      const paths = segment.paths;
      const path = paths[pathIndex];
      if (!path) {
        return;
      }

      updateUndoRedoStacks(state);

      let prevPath;
      let beforePoints = rawBeforePoints ? [...rawBeforePoints] : undefined;
      let afterPoints = rawAfterPoints ? [...rawAfterPoints] : undefined;

      // Determine the previous path, before points, and after points based on the type of handle being updated
      if (isStart) {
        afterPoints ||= [[latitude, longitude], path.points[path.points.length - 1]];
      } else if (isEnd) {
        prevPath = paths[pathIndex];
        beforePoints ||= [path.points[0], [latitude, longitude]];
      } else {
        prevPath = paths[pathIndex - 1];
        beforePoints ||= [prevPath.points[0], [latitude, longitude]];
        afterPoints ||= [[latitude, longitude], path.points[path.points.length - 1]];
      }

      // Update the points before the handle
      if (beforePoints) {
        // If the first point of the new previous path is not the first point of the old previous path, insert the first point of the old previous path into the beginning of the new previous path
        if (!isEqual(beforePoints[0], prevPath.points[0])) {
          beforePoints.unshift(prevPath.points[0]);
        }

        prevPath.points = beforePoints;
        prevPath.freeDraw = !rawBeforePoints;
      }

      // Update the points after the handle
      if (afterPoints) {
        // If the last point of the new path is not the first point of the next path, insert the first point of the next path into the end of the new path
        if (!isEqual(afterPoints[afterPoints.length - 1], path.points[path.points.length - 1])) {
          afterPoints.push(path.points[path.points.length - 1]);
        }

        path.points = afterPoints;
        path.freeDraw = !rawAfterPoints;
      }

      // Note that we intentionally do not update the selected handle index after updating a segment handle since it should remain the same
      handleRouteEdit(state);
    },
    updateEndPointOfPreviousLinkedSegment: (
      state,
      action: PayloadAction<{
        addToUndoRedoStacks?: boolean;
        latitude: number;
        longitude: number;
        points?: LatLng[];
        updateUndoRedo?: boolean;
      }>
    ) => {
      const { addToUndoRedoStacks, latitude, longitude, points } = action.payload;

      if (!isLinkedToPreviousSegment(state.route.segments, state.activeSegmentIndex)) {
        return;
      }

      const previousSegment = state.route.segments[state.activeSegmentIndex! - 1];

      const lastPath = previousSegment.paths[previousSegment.paths.length - 1];
      if (!lastPath || lastPath.points.length === 0) {
        return;
      }

      if (addToUndoRedoStacks) {
        updateUndoRedoStacks(state);
      }

      const newPoints = points ?? [lastPath.points[0], [latitude, longitude]];
      lastPath.points = newPoints;
      lastPath.freeDraw = !points;

      // Note that we intentionally do not update the selected handle index after updating a segment handle since it should remain the same
      handleRouteEdit(state);
    },
    updateStartPointOfNextLinkedSegment: (
      state,
      action: PayloadAction<{
        addToUndoRedoStacks?: boolean;
        latitude: number;
        longitude: number;
        points?: LatLng[];
      }>
    ) => {
      const { addToUndoRedoStacks, latitude, longitude, points } = action.payload;

      if (!isLinkedToNextSegment(state.route.segments, state.activeSegmentIndex)) {
        return;
      }

      const nextSegment = state.route.segments[state.activeSegmentIndex! + 1];

      const firstPath = nextSegment.paths[0];
      if (!firstPath || firstPath.points.length === 0) {
        return;
      }

      if (addToUndoRedoStacks) {
        updateUndoRedoStacks(state);
      }

      const newPoints = points ?? [[latitude, longitude], firstPath.points[firstPath.points.length - 1]];
      firstPath.points = newPoints;
      firstPath.freeDraw = !points;

      // Note that we intentionally do not update the selected handle index after updating a segment handle since it should remain the same
      handleRouteEdit(state);
    },
    removeActiveSegmentHandle: (state, action: PayloadAction<LatLng[] | undefined>) => {
      const rawReplacementPoints = action.payload;

      const activeSegmentIndex = state.activeSegmentIndex;
      if (activeSegmentIndex === undefined) {
        return;
      }

      const segment = state.route.segments[activeSegmentIndex];
      if (!segment) {
        return;
      }

      const selectedHandleIndex = state.selectedHandleIndex;
      if (selectedHandleIndex === undefined || selectedHandleIndex > segment.paths.length) {
        return;
      }

      const isStart = selectedHandleIndex === 0;
      const isEnd = selectedHandleIndex === segment.paths.length;
      const pathIndex = isEnd ? segment.paths.length - 1 : selectedHandleIndex;
      const path = segment.paths[pathIndex];
      if (!path) {
        return;
      }

      updateUndoRedoStacks(state);

      // Update linked state before actually editing the segment
      if (isStart && activeSegmentIndex > 0) {
        // If removing the start point, unlink the previous segment
        const previousSegment = state.route.segments[activeSegmentIndex - 1];
        if (previousSegment.isLinkedToNextSegment) {
          previousSegment.isLinkedToNextSegment = false;
        }
      } else if (isEnd && segment.isLinkedToNextSegment) {
        if (segment.isLinkedToNextSegment) {
          // If removing the end point, unlink the current segment
          segment.isLinkedToNextSegment = false;
        }
      }

      if (segment.paths.length === 1 && path.points.length > 1) {
        // If the segment only has one path and it has more than one point there are only start and end handles
        // If removing the start handle remove all but the end point
        // If removing the end handle remove all but the start point
        segment.paths[0].points = isStart ? [path.points[path.points.length - 1]] : [path.points[0]];
      } else if (isStart || isEnd) {
        // If removing the start or end point, remove the path at the specified index
        segment.paths.splice(pathIndex, 1);
      } else {
        // If removing a handle, replace the path at the specified index and the path before it with a new path connecting the first point of the path before it to the last point of the path at the specified index
        // Since this is not the start point, we know that the path before it exists
        const startPoint = segment.paths[pathIndex - 1].points[0];
        const endPoint = segment.paths[pathIndex].points[segment.paths[pathIndex].points.length - 1];
        const replacementPoints = rawReplacementPoints ? [...rawReplacementPoints] : [startPoint, endPoint];

        // If the replacement points don't start with the start point, add it to the beginning of the path
        if (!isEqual(replacementPoints[0], startPoint)) {
          replacementPoints.unshift(startPoint);
        }

        // If the replacement points don't end with the end point, add it to the end of the path
        if (!isEqual(replacementPoints[replacementPoints.length - 1], endPoint)) {
          replacementPoints.push(endPoint);
        }

        // Remove the path at the specified index
        segment.paths.splice(pathIndex, 1);

        if (replacementPoints.every(point => isEqual(point, replacementPoints[0]))) {
          // If the replacement points are all the same, remove the previous path as well
          segment.paths.splice(pathIndex - 1, 1);
        } else {
          // Otherwise replace the path before the removed path with the new path
          segment.paths[pathIndex - 1] = { points: replacementPoints, freeDraw: !rawReplacementPoints };
        }
      }

      updateSegmentMetadata(segment);
      updateRouteMetadata(state);
      // If the start point was removed select the new start point, else select the handle before the removed handle
      updateSelectedHandleIndex(state, selectedHandleIndex === 0 ? 0 : selectedHandleIndex - 1);
      handleRouteEdit(state);
    },
    reverseActiveSegment: state => {
      const segmentIndex = state.activeSegmentIndex;
      if (segmentIndex === undefined) {
        return;
      }

      const segment = state.route.segments[segmentIndex];
      if (!segment) {
        return;
      }

      updateUndoRedoStacks(state);

      segment.paths.reverse();
      segment.paths.forEach(path => {
        path.points.reverse();
      });

      state.reverseCount += 1;

      // Unlink the reversed segment and the segment before it
      if (segment.isLinkedToNextSegment) {
        segment.isLinkedToNextSegment = false;
      }
      if (segmentIndex > 0) {
        const previousSegment = state.route.segments[segmentIndex - 1];
        if (previousSegment.isLinkedToNextSegment) {
          previousSegment.isLinkedToNextSegment = false;
        }
      }

      if (state.selectedHandleIndex !== undefined) {
        const newSelectedHandleIndex = segment.paths.length - state.selectedHandleIndex;
        updateSelectedHandleIndex(state, newSelectedHandleIndex);
      }

      handleRouteEdit(state);
    },
    reverseCustomRoute: state => {
      const segments = state.route.segments;

      if (segments.length === 0) {
        return;
      }

      updateUndoRedoStacks(state);

      segments.reverse();
      segments.forEach((segment, index) => {
        segment.paths.reverse();
        segment.paths.forEach(path => {
          path.points.reverse();
        });

        // Update the linked state of each segment since the "next" segment has changed.
        // Each segment's linked state should reflect the segment that comes after it in the new order.
        // The last segment will always be unlinked.
        if (index === segments.length - 1) {
          if (segment.isLinkedToNextSegment) {
            segment.isLinkedToNextSegment = false;
          }
        } else {
          const nextSegment = segments[index + 1];
          if (nextSegment.isLinkedToNextSegment) {
            segment.isLinkedToNextSegment = nextSegment.isLinkedToNextSegment;
          }
        }
      });

      // Ideally we would update the active segment to maintain the state for the user.
      // However, since we just keep track of the active segment index, if a user were to undo the reversal,
      // the order of the segments would flip without updating the active segment index appropriately.
      // Instead, just clear the active segment index since the user took a global action anyways.
      state.activeSegmentIndex = undefined;
      state.selectedHandleIndex = undefined;

      state.reverseCount += 1;

      handleRouteEdit(state);
    },
    doubleBackActiveSegment: state => {
      const segmentIndex = state.activeSegmentIndex;
      if (segmentIndex === undefined) {
        return;
      }

      const segment = state.route.segments[segmentIndex];

      // If the segment has fewer than 2 points, do nothing
      if (!segment || segment.paths.length === 0 || segment.paths[0].points.length < 2) {
        return;
      }

      const startPoint = segment.paths[0].points[0];
      const endPath = segment.paths[segment.paths.length - 1];
      const endPoint = endPath.points[endPath.points.length - 1];
      // if the start and end points are the same, do nothing
      if (isEqual(startPoint, endPoint)) {
        return;
      }

      updateUndoRedoStacks(state);

      const returnPaths = [...segment.paths.map(path => ({ ...path, points: [...path.points] }))];
      returnPaths.reverse();
      returnPaths.forEach(path => {
        path.points.reverse();
      });
      segment.paths.push(...returnPaths);

      // Unlink the active segment since users would have very few use cases for doubling back and wanting to update the next segment's start point
      if (segment.isLinkedToNextSegment) {
        segment.isLinkedToNextSegment = false;
      }

      // Select the new end point of the segment
      updateSelectedHandleIndex(state, segment.paths.length);
      handleRouteEdit(state);
    },
    closeLoopActiveSegment: (state, action: PayloadAction<LatLng[] | undefined>) => {
      const rawPoints = action.payload;

      const segmentIndex = state.activeSegmentIndex;
      if (segmentIndex === undefined) {
        return;
      }

      const segment = state.route.segments[segmentIndex];

      // If the segment has fewer than 2 points, do nothing
      if (!segment || segment.paths.length === 0 || segment.paths[0].points.length < 2) {
        return;
      }

      const startPoint = segment.paths[0].points[0];
      const endPath = segment.paths[segment.paths.length - 1];
      const endPoint = endPath.points[endPath.points.length - 1];

      // If the start and end points are the same, do nothing
      if (isEqual(startPoint, endPoint)) {
        return;
      }

      updateUndoRedoStacks(state);

      const points = rawPoints ? [...rawPoints] : [endPoint, startPoint];

      // If the first point of the new path is not the last point of the previous path, insert the last point of the previous path into the beginning of the new path
      if (!isEqual(points[0], endPoint)) {
        points.unshift(endPoint);
      }

      // If the last point of the new path is not the start point, insert the start point into the end of the new path
      if (!isEqual(points[points.length - 1], startPoint)) {
        points.push(startPoint);
      }

      const path = { points, freeDraw: !rawPoints };
      segment.paths.push(path);

      // Unlink the active segment since users would have very few use cases for closing the loop and wanting to update the next segment's start point
      if (segment.isLinkedToNextSegment) {
        segment.isLinkedToNextSegment = false;
      }

      // Select the new end point of the segment
      updateSelectedHandleIndex(state, segment.paths.length);
      handleRouteEdit(state);
    },
    linkActiveSegmentToNextSegment: (state, action: PayloadAction<{ points: LatLng[]; freeDraw: boolean }>) => {
      const { points, freeDraw } = action.payload;

      const segmentIndex = state.activeSegmentIndex;
      if (segmentIndex === undefined) {
        return;
      }

      const segment = state.route.segments[segmentIndex!];
      segment.isLinkedToNextSegment = true;

      const isSamePoint = isEqual(points[0], points[points.length - 1]);
      if (isSamePoint) {
        // If there are no points to update, just link the segment but don't add any paths or to the undo stack
        return;
      }

      updateUndoRedoStacks(state);

      const path = { points, freeDraw };
      segment.paths.push(path);

      // Select the new end point of the segment
      updateSelectedHandleIndex(state, segment.paths.length);
      handleRouteEdit(state);
    },
    linkActiveSegmentToPreviousSegment: (state, action: PayloadAction<{ points: LatLng[]; freeDraw: boolean }>) => {
      const { points, freeDraw } = action.payload;

      const segmentIndex = state.activeSegmentIndex;
      if (segmentIndex === undefined) {
        return;
      }

      const segment = state.route.segments[segmentIndex!];
      const previousSegment = state.route.segments[segmentIndex! - 1];
      previousSegment.isLinkedToNextSegment = true;

      const isSamePoint = isEqual(points[0], points[points.length - 1]);
      if (isSamePoint) {
        // If there are no points to update, just link the segment but don't add any paths or to the undo stack
        return;
      }

      updateUndoRedoStacks(state);

      const path = { points, freeDraw };
      segment.paths.unshift(path);

      // Select the new start point of the segment
      updateSelectedHandleIndex(state, 0);
      handleRouteEdit(state);
    },
    unlinkSegmentAtIndexFromNextSegment: (state, action: PayloadAction<number>) => {
      const linkedSegment = state.route.segments[action.payload];
      if (linkedSegment.isLinkedToNextSegment) {
        linkedSegment.isLinkedToNextSegment = false;
      }
    },
    unlinkActiveSegmentFromPreviousSegment: state => {
      const { activeSegmentIndex, route } = state;
      const segments = route.segments;

      if (isLinkedToPreviousSegment(segments, activeSegmentIndex)) {
        const previousSegment = segments[activeSegmentIndex! - 1];
        if (previousSegment.isLinkedToNextSegment) {
          previousSegment.isLinkedToNextSegment = false;
        }
      }
    },
    updateMetadata: (state, action: PayloadAction<Metadata & { segmentsMetadata: Record<number, SegmentMetadata> }>) => {
      const { segmentsMetadata, elevationGain, elevationLoss, length: distance, averageTimeToComplete: estimatedTime } = action.payload;

      // Note that we intentionally don't add this to the undo stack.
      // Since metadata is updated as a side effect of modifying segments performing an undo or redo will cause the metadata to be updated for the restored segments
      // Since metadata fetching is cached if the restored segments have already had metadata fetched we will use the cached metadata

      Object.keys(segmentsMetadata).forEach(stringIndex => {
        const index = Number(stringIndex);
        const segmentMetadata = segmentsMetadata[index];
        const { elevations, elevationGain, elevationLoss, length: distance, averageTimeToComplete: estimatedTime, startLocation } = segmentMetadata;
        const segment = state.route.segments[index];
        if (segment) {
          let elevationPoints: ElevationPoint[] | undefined;
          const points = segment.paths
            .map(path => path.points)
            .flat()
            .map(([lat, lng]) => [lng, lat]);

          // We can only calculate elevation points if there are at least two points in the segment
          if (points.length > 1) {
            // Add the total distance before the active segment to each point in the active segment returned from the API and interpolate the coordinates
            const line = lineString(
              segment.paths
                .map(path => path.points)
                .flat()
                .map(([lat, lng]) => [lng, lat])
            );
            elevationPoints = elevations?.map((elevation, index) => {
              const distanceTo = (index / (elevations.length - 1)) * (distance || 0);
              const coordinates = along(line, distanceTo, { units: 'meters' }).geometry.coordinates;
              return [distanceTo, elevation, [coordinates[1], coordinates[0]]] as ElevationPoint;
            });
          }

          segment.elevationPoints = elevationPoints;
          segment.distance = distance ?? undefined;
          segment.elevationGain = elevationGain ?? undefined;
          segment.elevationLoss = elevationLoss ?? undefined;
          segment.estimatedTime = estimatedTime || undefined;
          segment.startLocation = startLocation;
        }
      });

      state.route.elevationGain = elevationGain ?? undefined;
      state.route.elevationLoss = elevationLoss ?? undefined;
      state.route.distance = distance ?? undefined;
      state.route.estimatedTime = estimatedTime || undefined;
    },

    setWaypointEditingState: (
      state,
      action: PayloadAction<{
        activeId?: CustomRouteWaypointId | undefined;
        selectedId?: CustomRouteWaypointId | undefined;
        isEditing?: boolean;
      }>
    ) => {
      const { activeId, selectedId, isEditing } = action.payload;
      const isEditingWaypoint = isEditing ?? selectedId !== undefined;
      state.isEditingWaypoint = isEditingWaypoint;
      state.selectedWaypointId = selectedId;
      state.activeWaypointId = selectedId === undefined ? activeId : undefined;

      if (isEditingWaypoint) {
        state.isEditing = true;

        // Capture the starting position when editing begins
        if (selectedId !== undefined) {
          const waypoint = state.route.waypoints.find(wp => wp.id === selectedId);
          if (waypoint) {
            state.waypointEditStartPosition = {
              id: selectedId,
              latitude: waypoint.latitude,
              longitude: waypoint.longitude
            };
          }
        }
      }
    },
    clearWaypointEditingState: state => {
      if (state.selectedWaypointId !== undefined) {
        state.selectedWaypointId = undefined;
      }
      if (state.activeWaypointId !== undefined) {
        state.activeWaypointId = undefined;
      }
      if (state.isEditingWaypoint) {
        state.isEditingWaypoint = false;
      }
      state.waypointEditStartPosition = null;
    },
    setViewingWaypointId: (
      state,
      action: PayloadAction<{ id: CustomRouteWaypointId; entryPoint: WaypointEntryPoint; sourceView: WaypointSourceView }>
    ) => {
      state.viewingWaypointId = action.payload.id;
      state.activeWaypointId = action.payload.id;
      state.viewingWaypointEntryPoint = action.payload.entryPoint;
      state.viewingWaypointSourceView = action.payload.sourceView;
    },
    clearViewingWaypointId: state => {
      state.viewingWaypointId = undefined;
      state.activeWaypointId = undefined;
      state.viewingWaypointEntryPoint = undefined;
      state.viewingWaypointSourceView = undefined;
    },
    addWaypoint: (state, action: PayloadAction<Omit<CustomRouteWaypoint, 'id'>>) => {
      state.isEditing = true;

      const id = state.nextWaypointId || 0;
      state.route.waypoints.push({ ...action.payload, id });

      state.activeWaypointId = id;
      state.selectedWaypointId = id;
      state.nextWaypointId = id + 1;
    },
    updateWaypoint: (
      state,
      action: PayloadAction<{
        id: CustomRouteWaypointId;
        name: string;
        description: string;
        latitude: number;
        longitude: number;
        showTitle: boolean;
        waypointCategory?: WaypointCategory;
        contentPrivacy?: Visibility;
        isGlobal?: boolean;
      }>
    ) => {
      state.isEditing = true;

      const { id, ...updates } = action.payload;

      const waypointIndex = state.route.waypoints.findIndex(waypoint => waypoint.id === id);
      if (waypointIndex !== -1) {
        const currentWaypoint = state.route.waypoints[waypointIndex];
        const isNewWaypoint = currentWaypoint.isTemporary;

        // Check if position changed compared to the starting position (when editing began)
        // If no starting position is tracked, compare to current position
        let positionChanged = false;
        if (state.waypointEditStartPosition && state.waypointEditStartPosition.id === id) {
          positionChanged =
            state.waypointEditStartPosition.latitude !== updates.latitude || state.waypointEditStartPosition.longitude !== updates.longitude;
        } else {
          positionChanged = currentWaypoint.latitude !== updates.latitude || currentWaypoint.longitude !== updates.longitude;
        }

        // Add to undo stack if:
        // 1. This is a new waypoint being saved for the first time (temporary → permanent) OR
        // 2. Position changed (not just name/description)
        const shouldAddToUndo = isNewWaypoint || positionChanged;

        if (shouldAddToUndo) {
          // For new waypoints, we need to save state WITHOUT the new waypoint
          // For moved waypoints, restore starting position before saving to undo stack
          if (isNewWaypoint) {
            // For new waypoints, manually create undo entry without the new waypoint
            const waypointsWithoutNew = state.route.waypoints.filter((_, index) => index !== waypointIndex);

            state.undoStack.push(
              JSON.parse(
                JSON.stringify({
                  aiGeneratedDescriptionsSegmentsSnapshot: state.aiGeneratedDescriptionsSegmentsSnapshot,
                  segments: state.route.segments,
                  activityType: state.route.activityType,
                  costings: state.costings,
                  waypoints: waypointsWithoutNew
                })
              )
            );
            state.redoStack = [];

            if (state.undoStack.length > UNDO_LIMIT) {
              state.undoStack.shift();
            }
          } else if (positionChanged && state.waypointEditStartPosition && state.waypointEditStartPosition.id === id) {
            // Temporarily restore starting position before saving to undo stack
            state.route.waypoints[waypointIndex] = {
              ...currentWaypoint,
              latitude: state.waypointEditStartPosition.latitude,
              longitude: state.waypointEditStartPosition.longitude
            };
            updateUndoRedoStacks(state);
          } else {
            updateUndoRedoStacks(state);
          }

          // Now apply the updates (including removing isTemporary flag)
          state.route.waypoints[waypointIndex] = {
            ...state.route.waypoints[waypointIndex],
            ...updates,
            isTemporary: undefined
          };
        } else {
          // No position change and not a new waypoint, just update the waypoint
          state.route.waypoints[waypointIndex] = {
            ...state.route.waypoints[waypointIndex],
            ...updates
          };
        }

        // Clear the starting position after saving
        state.waypointEditStartPosition = null;
      }
    },
    updateWaypointDraft: (state, action: PayloadAction<Partial<CustomRouteWaypoint> | null>) => {
      state.draftWaypoint = action.payload;
    },
    updateWaypointPosition: (
      state,
      action: PayloadAction<{
        id: CustomRouteWaypointId;
        latitude: number;
        longitude: number;
      }>
    ) => {
      // Update waypoint position without adding to undo stack
      // This is used for intermediate position updates while dragging in the edit drawer
      const { id, latitude, longitude } = action.payload;
      const waypointIndex = state.route.waypoints.findIndex(waypoint => waypoint.id === id);
      if (waypointIndex !== -1) {
        state.route.waypoints[waypointIndex] = {
          ...state.route.waypoints[waypointIndex],
          latitude,
          longitude
        };
      }
    },
    removeWaypoint: (state, action: PayloadAction<CustomRouteWaypointId>) => {
      const removeId = action.payload;
      const waypoint = state.route.waypoints.find(wp => wp.id === removeId);

      // Don't add temporary waypoints to undo stack
      if (waypoint && !waypoint.isTemporary) {
        updateUndoRedoStacks(state);
      }

      if (waypoint?.isTemporary && state.nextWaypointId !== undefined) {
        state.nextWaypointId -= 1;
      }

      state.isEditing = true;

      // Remove the waypoint without re-indexing IDs (keeps IDs stable for undo/redo)
      state.route.waypoints = state.route.waypoints.filter(waypoint => waypoint.id !== removeId);

      // If the last waypoint was removed, clear the active waypoint
      if (state.activeWaypointId === removeId) {
        state.activeWaypointId = undefined;
      }
    },
    // Replace the route's waypoints array without touching isEditing or undo/redo.
    // Used by callers that drive their own server-backed edits (e.g. recordings)
    // and need an optimistic-update primitive without the route-builder side effects.
    setRouteWaypoints: (state, action: PayloadAction<CustomRouteWaypoint[]>) => {
      state.route.waypoints = action.payload;
      if (state.activeWaypointId !== undefined && !action.payload.some(wp => wp.id === state.activeWaypointId)) {
        state.activeWaypointId = undefined;
      }
    },
    setToastMessage: (state, action: PayloadAction<ToastMessage | undefined>) => {
      state.toastMessage = action.payload;
    },
    setToastAlerts: (state, action: PayloadAction<CustomRouteAlert[]>) => {
      state.toastAlerts = [...action.payload];
    },
    clearTopToastAlert: state => {
      state.toastAlerts.shift();
    },
    undo: state => {
      const prevState = state.undoStack?.pop();
      if (!prevState) {
        return;
      }

      // Save current state to redo stack with full waypoint data
      state.redoStack.push({
        aiGeneratedDescriptionsSegmentsSnapshot: state.aiGeneratedDescriptionsSegmentsSnapshot,
        segments: state.route.segments,
        activityType: state.route.activityType,
        costings: state.costings,
        waypoints: state.route.waypoints
      });

      // Restore state from undo stack
      state.route.segments = prevState.segments;
      state.route.activityType = prevState.activityType;
      state.costings = prevState.costings;
      state.aiGeneratedDescriptionsSegmentsSnapshot = prevState.aiGeneratedDescriptionsSegmentsSnapshot;

      // Bump on every undo so a reverse that is being undone busts potentially cached chevron symbols.
      state.reverseCount += 1;

      // Restore waypoints with full waypoint data
      const currentWaypointsById = new Map(state.route.waypoints.map(wp => [wp.id, wp]));

      state.route.waypoints = (prevState.waypoints || []).map(savedWaypoint => {
        const currentWaypoint = currentWaypointsById.get(savedWaypoint.id);

        if (currentWaypoint) {
          // Waypoint still exists - preserve current name/description, restore position
          return {
            ...currentWaypoint,
            latitude: savedWaypoint.latitude,
            longitude: savedWaypoint.longitude
          };
        }

        // Waypoint was deleted - restore it with full saved data from undo stack
        return { ...savedWaypoint };
      });

      updateNextSegmentColor(state);
      updateNextWaypoint(state);
      updateRouteMetadata(state);
      updateActiveSegmentIndex(state);
      updateSelectedHandleIndex(state);
    },
    redo: state => {
      const nextState = state.redoStack?.pop();
      if (!nextState) {
        return;
      }

      // Save current state to undo stack with full waypoint data
      state.undoStack.push({
        aiGeneratedDescriptionsSegmentsSnapshot: state.aiGeneratedDescriptionsSegmentsSnapshot,
        segments: state.route.segments,
        activityType: state.route.activityType,
        costings: state.costings,
        waypoints: state.route.waypoints
      });

      // Restore state from redo stack
      state.route.segments = nextState.segments;
      state.route.activityType = nextState.activityType;
      state.costings = nextState.costings;
      state.aiGeneratedDescriptionsSegmentsSnapshot = nextState.aiGeneratedDescriptionsSegmentsSnapshot;

      // Bump on every redo so a re-applied reverse busts potentially cached chevron symbols
      state.reverseCount += 1;

      // Restore waypoints with full waypoint data
      const currentWaypointsById = new Map(state.route.waypoints.map(wp => [wp.id, wp]));

      state.route.waypoints = (nextState.waypoints || []).map(savedWaypoint => {
        const currentWaypoint = currentWaypointsById.get(savedWaypoint.id);

        if (currentWaypoint) {
          // Waypoint still exists - preserve current name/description, restore position
          return {
            ...currentWaypoint,
            latitude: savedWaypoint.latitude,
            longitude: savedWaypoint.longitude
          };
        }

        // Waypoint was deleted - restore it with full saved data from redo stack
        return { ...savedWaypoint };
      });

      updateNextSegmentColor(state);
      updateNextWaypoint(state);
      updateRouteMetadata(state);
      updateActiveSegmentIndex(state);
      updateSelectedHandleIndex(state);
    },
    resetUndoRedo: state => {
      state.undoStack = [];
      state.redoStack = [];
    },
    setCostingsConfig: (state, action: PayloadAction<CostingsConfig>) => {
      state.costingsConfig = action.payload;
      resetCostings(state);
    },
    setCostings: (state, action: PayloadAction<Costings>) => {
      state.costings = action.payload;
    },
    setSuggestedRoute: (state, action: PayloadAction<LineString | undefined>) => {
      state.suggestedRoute = action.payload;
    },
    setAiGeneratedDescriptionsSegmentsSnapshot: (state, action: PayloadAction<CustomRouteSegment[] | undefined>) => {
      state.aiGeneratedDescriptionsSegmentsSnapshot = action.payload ? getSegmentsPathSnapshots(action.payload) : undefined;
    },
    setSplitType: (state, action: PayloadAction<RouteAssistantSplit>) => {
      state.splitType = action.payload;
      state.routeEditedAfterSplit = false;
    },
    setEditEndpointMode: (state, action: PayloadAction<boolean>) => {
      state.editEndpointMode = action.payload;
      if (!action.payload) {
        state.pendingEndpointCrop = null;
      }
    },
    setPendingEndpointCrop: (state, action: PayloadAction<EndpointCrop | null>) => {
      state.pendingEndpointCrop = action.payload;
    },
    setIsRouteBuildingEnabled: (state, action: PayloadAction<boolean>) => {
      state.isRouteBuildingEnabled = action.payload;
    }
  }
});

export const {
  resetState,
  setEditState,
  setIsSmartRouting,
  setSuggestedRoute,
  setRoute,
  updateRoute,
  setCutIndex,
  updateRouting,
  updateIsRoutingAlongSuggestedRoute,
  setRouteDetails,
  setRoutePrivacy,
  updateRecordingReview,
  setRecordingPhotos,
  setIsTrailMap,
  setActiveSegmentIndex,
  setHoveredSegmentIndex,
  setSelectedHandleIndex,
  addSegment,
  splitActiveSegmentAtSelectedHandle,
  removeSegment,
  reorderSegments,
  replaceSegments,
  replaceSegmentDescriptionsAndAlerts,
  setSegmentDetails,
  addActiveSegmentPath,
  addActiveSegmentHandle,
  updateActiveSegmentHandle,
  updateEndPointOfPreviousLinkedSegment,
  updateStartPointOfNextLinkedSegment,
  removeActiveSegmentHandle,
  reverseActiveSegment,
  reverseCustomRoute,
  doubleBackActiveSegment,
  closeLoopActiveSegment,
  linkActiveSegmentToNextSegment,
  linkActiveSegmentToPreviousSegment,
  unlinkSegmentAtIndexFromNextSegment,
  unlinkActiveSegmentFromPreviousSegment,
  updateMetadata,
  setWaypointEditingState,
  clearWaypointEditingState,
  setViewingWaypointId,
  clearViewingWaypointId,
  addWaypoint,
  updateWaypoint,
  removeWaypoint,
  setRouteWaypoints,
  updateWaypointDraft,
  updateWaypointPosition,
  setToastMessage,
  setToastAlerts,
  clearTopToastAlert,
  undo,
  redo,
  resetUndoRedo,
  setCostingsConfig,
  setCostings,
  setAiGeneratedDescriptionsSegmentsSnapshot,
  setSplitType,
  setEditEndpointMode,
  setPendingEndpointCrop,
  setIsRouteBuildingEnabled
} = customRouteSlice.actions;

export const customRouteReducer = customRouteSlice.reducer;
